import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import Button from '@material-ui/core/Button';
import type { GridApi } from 'ag-grid-community';
import { useQueryClient } from 'react-query';

import {
  AgGridWidgetWrapper,
  Watermark,
  WidgetHeader,
} from '@mummssoftware/common-ui';
import type { PatientChatPatientSummary } from '@mummssoftware/utils';

import { useMaiaChatAgentContext } from '../../context';
import { useConversationHistory } from '../../hooks/useConversationHistory';
import {
  questionMessages,
  toChatMessages,
  useConversationMessages,
} from '../../hooks/useConversationMessages';
import { useMaiaChat } from '../../hooks/useMaiaChat';
import { usePatientChatPatients } from '../../hooks/usePatientChatPatients';
import { maiaQueryKeys } from '../../queryClient';
import { withRequestTimeouts } from '../../utils/requestTimeout';
import {
  defaultDateRange,
  isStartDateAllowed,
  parseReferralDate,
  rangeFromApi,
  startDateBounds,
} from '../../utils/dateRange';
import {
  formatShortDate,
  pastSummaryLabel,
  questionSummaryLabel,
} from '../../utils/formatters';
import defaults from '../../translations/en.json';
import ActiveChatView from './ActiveChatView';
import { HistoryGrid } from './HistoryGrid';
import { MaiaHeaderControls, StartChatAction } from './MaiaHeaderControls';
import PastChatView from './PastChatView';
import { PatientChatsDialog } from './PatientChatsDialog';
import { PatientListGrid } from './PatientListGrid';
import { useQuestionDateFilter } from './QuestionDateFilter';
import { SeeTokensChip } from './SeeTokensChip';

type MaiaChatAgentWidgetProps = {
  embedded?: boolean;
  smallWidget?: boolean;
  /** The dashboard's widgetName; the header uses fixed titles instead. */
  appTitle?: string;
  onClick?: () => void;
  onRemove?: () => void;
  doNotShow?: boolean;
  /**
   * Inside MAIA Chats' patient dialog: no expand/close icons, a fixed height,
   * and back to history on close.
   */
  drillIn?: boolean;
};

/** Body height in the MAIA Chats patient dialog (the dashboard's full-size height does not apply there). */
const DRILL_IN_BODY_STYLE = { height: '70vh', maxHeight: '70vh' };

const AppRoot = styled.div<{ embedded?: boolean }>`
  margin-left: var(--safe-area-inset-left);
  margin-right: var(--safe-area-inset-right);
  margin-bottom: var(--safe-area-inset-bottom);
  overflow: hidden;
  ${(props) => (props.embedded ? `height: initial;` : `height: 100vh;`)}
  display: flex;
  /* no background here: the tile shows through WidgetHeader's 2px bottom
     margin, which is the header/body separator every other widget has */
  @media print {
    display: block;
    height: auto;
  }
`;

const AppContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
  @media print {
    height: auto;
  }
`;

/** Fills AgGridWidgetWrapper (fixed tile / expanded height) so views can flex. */
const Body = styled.div`
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const HistoryPane = styled.div<{ $hidden: boolean }>`
  display: ${(props) => (props.$hidden ? 'none' : 'flex')};
  flex: 1;
  flex-direction: column;
  min-height: 0;
`;

const StatusBody = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: 16px;
  text-align: center;
  color: ${(props) => props.theme.palette.text.primary};
`;

const ClearFiltersButton = styled(Button)`
  && {
    height: 24px;
    min-width: 0;
    padding: 0 9px;
    font-size: 12px;
    line-height: 1;
    text-transform: none;
    white-space: nowrap;
  }
`;

const Banner = styled.p`
  margin: 0;
  padding: 6px 10px;
  font-size: 0.8rem;
  color: ${(props) => props.theme.palette.error?.main || '#f44336'};
`;

/**
 * MAIA widget: shared WidgetHeader with the MAIA controls as children, and a
 * body that is one of history (default and empty state), chat, or a read-only
 * past conversation. The tile and its expanded copy share one state (useMaiaChat).
 * With no patient (clinical dashboard) it is MAIA Chats instead: one row per
 * patient with MAIA chats, like HOPE and Forms switch on the patient.
 */
const MaiaChatAgentWidget = ({
  embedded,
  smallWidget,
  onClick,
  onRemove,
  doNotShow,
  drillIn,
}: MaiaChatAgentWidgetProps) => {
  const {
    watermark,
    patient,
    patientNumber,
    agency,
    siteId,
    currentUser,
    readOnly,
    isMummsAdmin,
    bootstrapError,
    questionDateFilter: listFilter,
    agentGateway: rawGateway,
  } = useMaiaChatAgentContext();
  // every call but the answer itself is time-boxed, so the widget never waits on a hung gateway
  const agentGateway = useMemo(() => withRequestTimeouts(rawGateway), [
    rawGateway,
  ]);
  const siteTimezone = currentUser?.siteTimezone ?? null;
  // The date filter over the grid's counts, expanded only: the tile always
  // shows every date. MAIA Chats' patient dialog uses the list's filter, so
  // the list behind it always has the same dates.
  const ownFilter = useQuestionDateFilter(siteTimezone);
  const dateFilter = listFilter || ownFilter;
  const shownFilter = smallWidget ? null : dateFilter;
  const filterRange = shownFilter ? shownFilter.range : null;
  // Shared with the other copy (tile or expanded) of this patient's widget.
  const chat = useMaiaChat(agency, patientNumber, agentGateway);
  const { view, pastRow, pastQuestion } = chat.state;
  // Reloads when the history comes back from a chat (a new conversation);
  // the past view's Done finds it as it left it, with no new request.
  const history = useConversationHistory(
    agentGateway,
    agency,
    patientNumber,
    view !== 'chat',
    filterRange,
  );
  // A whole conversation is loaded; one of its questions is already in hand.
  const past = useConversationMessages(
    agentGateway,
    view === 'past' && pastRow && !pastQuestion ? pastRow : null,
  );
  const isPatientList = !patientNumber && !bootstrapError;
  const patientList = usePatientChatPatients(
    agentGateway,
    agency,
    siteId || undefined,
    isPatientList,
    filterRange,
  );
  // The list's grid api, so the header's Clear filters can reach the grid.
  const patientGridApi = useRef<GridApi | null>(null);
  const [hasFilters, setHasFilters] = useState(false);
  const onPatientGridApi = useCallback((api: GridApi) => {
    patientGridApi.current = api;
  }, []);
  const clearFilters = () => {
    if (patientGridApi.current) {
      patientGridApi.current.setFilterModel(null);
    }
  };
  // The patient whose MAIA widget is open over the list.
  const [
    openPatient,
    setOpenPatient,
  ] = useState<PatientChatPatientSummary | null>(null);
  const queryClient = useQueryClient();
  const closePatient = useCallback(() => {
    setOpenPatient(null);
    // a chat started in the dialog changes that patient's counts
    queryClient.invalidateQueries(maiaQueryKeys.allPatients);
  }, [queryClient]);

  // Closing the dialog puts that patient back on history for the next open.
  useEffect(() => (drillIn ? () => chat.reset() : undefined), [chat, drillIn]);

  // Only a patient referred more than 365 days ago gets the start-date picker.
  const referral = parseReferralDate(patient?.referralDate);
  const bounds = startDateBounds(referral);

  const handleStart = () => {
    // Without the picker a chat covers the last 365 days, or from a more
    // recent referral date to today (the chat says so).
    const range = bounds ? chat.state.dateRange : defaultDateRange(referral);
    if (bounds && !isStartDateAllowed(range.fromDate, bounds)) {
      chat.setError(
        defaults.dateRangeErrorStart
          .replace('{min}', formatShortDate(bounds.minDate))
          .replace('{max}', formatShortDate(bounds.maxDate)),
      );
      return;
    }
    chat.startChat(range);
  };

  const { dateRange } = chat.state;
  const rangeNote = defaults.chatRangeNote
    .replace('{from}', formatShortDate(dateRange.fromDate))
    .replace('{to}', formatShortDate(dateRange.toDate));

  const showControls = Boolean(patientNumber) && !bootstrapError;
  const canStart = showControls && view === 'history' && !readOnly;
  // Mumms admins, expanded, over a grid: the token split and cost columns
  const showSeeTokens =
    Boolean(isMummsAdmin) &&
    !smallWidget &&
    (isPatientList || (showControls && view === 'history'));

  const handleRemove = () => {
    chat.reset();
    if (onRemove) {
      onRemove();
    }
  };

  // Fixed titles, whatever the dashboard's widgetName: MAIA Chats (clinical,
  // tile and expanded); patient: MAIA in the tile, MAIA Chat Agent expanded.
  let title = defaults.titleChatAgent;
  if (isPatientList) {
    title = defaults.titleChats;
  } else if (smallWidget) {
    title = defaults.title;
  }

  let body: React.ReactNode;
  if (bootstrapError) {
    body = <StatusBody>{bootstrapError}</StatusBody>;
  } else if (isPatientList) {
    body = (
      <PatientListGrid
        rows={patientList.rows}
        error={patientList.error}
        smallWidget={smallWidget}
        siteTimezone={siteTimezone}
        inDateRange={Boolean(filterRange)}
        dateFilter={shownFilter}
        onGridApi={onPatientGridApi}
        onFiltersChange={setHasFilters}
        onOpenPatient={setOpenPatient}
      />
    );
  } else if (view === 'chat') {
    body = (
      <ActiveChatView
        messages={chat.state.messages}
        rangeNote={rangeNote}
        isSending={chat.state.isSending}
        isSlowReply={chat.state.isSlowReply}
        isAgentBusy={chat.state.isAgentBusy}
        isCancelling={chat.state.isCancelling}
        onCancel={chat.cancelPending}
        onKeepWaiting={chat.keepWaiting}
        error={chat.state.error}
        onSend={chat.sendMessage}
        onRateAnswer={chat.rateAnswer}
        smallWidget={smallWidget}
        tokenNotice={chat.state.tokenNotice}
        onDismissTokenNotice={chat.dismissTokenNotice}
      />
    );
  } else if (view === 'past') {
    let summaryLabel: string | null = null;
    if (pastRow && pastQuestion) {
      summaryLabel = questionSummaryLabel(
        pastRow,
        pastQuestion,
        defaults.tokens,
        siteTimezone,
      );
    } else if (pastRow) {
      summaryLabel = pastSummaryLabel(pastRow, defaults.tokens, siteTimezone);
    }
    body = (
      <PastChatView
        summaryLabel={summaryLabel}
        messages={
          pastQuestion
            ? questionMessages(pastQuestion)
            : toChatMessages(past.stored)
        }
        isLoading={past.isLoading}
        error={past.error}
      />
    );
  }

  // Mounted for as long as there is a patient and only hidden behind a chat or
  // a past view, so Done returns to the same grid: same opened rows, scroll
  // and sort, and no request for questions already loaded.
  const historyPane =
    bootstrapError || isPatientList ? null : (
      <HistoryPane $hidden={view !== 'history'}>
        {chat.state.error ? (
          <Banner role="alert">{chat.state.error}</Banner>
        ) : null}
        <HistoryGrid
          agentGateway={agentGateway}
          rows={history.rows}
          error={history.error}
          onOpenPast={chat.openPast}
          smallWidget={smallWidget}
          siteTimezone={siteTimezone}
          dateFilter={shownFilter}
        />
      </HistoryPane>
    );

  return (
    <AppRoot embedded={embedded}>
      <AppContent>
        <WidgetHeader
          appTitle={title}
          versionInfo={defaults.title}
          smallWidget={smallWidget}
          onClick={onClick}
          onRemove={handleRemove}
          doNotShow={doNotShow}
          noIcon={drillIn}
          titleAction={
            canStart ? (
              <StartChatAction
                onStart={handleStart}
                isStarting={chat.state.isStarting}
              />
            ) : null
          }
        >
          {showSeeTokens ? <SeeTokensChip /> : null}
          {isPatientList && hasFilters ? (
            <ClearFiltersButton
              color="primary"
              onClick={clearFilters}
              data-testid="maia-clear-filters"
            >
              {defaults.clearFilters}
            </ClearFiltersButton>
          ) : null}
          {showControls ? (
            <MaiaHeaderControls
              view={view}
              smallWidget={smallWidget}
              dateRange={chat.state.dateRange}
              onDateRangeChange={chat.setDateRange}
              responseDetail={chat.state.responseDetail}
              onResponseDetailChange={chat.setResponseDetail}
              onDone={chat.done}
              isStarting={chat.state.isStarting}
              isSending={chat.state.isSending}
              pastRange={rangeFromApi(pastRow?.fromDate, pastRow?.toDate)}
              startDateBounds={bounds}
              noHeaderIcons={drillIn}
            />
          ) : null}
        </WidgetHeader>
        {watermark && watermark.show ? (
          <Watermark readOnly={watermark.readOnly} smallWidget={smallWidget} />
        ) : null}
        <AgGridWidgetWrapper
          smallWidget={smallWidget}
          style={drillIn ? DRILL_IN_BODY_STYLE : undefined}
        >
          <Body>
            {body}
            {historyPane}
          </Body>
        </AgGridWidgetWrapper>
      </AppContent>
      {isPatientList ? (
        <PatientChatsDialog
          row={openPatient}
          questionDateFilter={shownFilter}
          onBack={closePatient}
        >
          <MaiaChatAgentWidget embedded drillIn />
        </PatientChatsDialog>
      ) : null}
    </AppRoot>
  );
};

export default MaiaChatAgentWidget;
